import { useQuery } from '@tanstack/react-query'
import {
  CheckCircle2,
  Clock,
  Download,
  FileText,
  MessageSquarePlus,
  School,
  UserPlus,
  Users,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import { toApiError } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { ErrorState, LoadingState, NotFoundState } from '@/components/feedback/States'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Checkbox } from '@/components/ui/Checkbox'
import { DescriptionList } from '@/components/ui/DescriptionList'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { PageHeader } from '@/components/ui/PageHeader'
import { Textarea } from '@/components/ui/Textarea'
import {
  applicationQuery,
  applicationSectionsQuery,
  useAddApplicationNote,
  useChangeApplicationStatus,
  useEnrolApplication,
} from '@/features/admissions/api'
import { admissionStatusLabel, admissionStatusTone, GUARDIAN_RELATIONS } from '@/features/admissions/status'
import type { AdmissionStatus, ApplicationDetail } from '@/features/admissions/types'
import { formatBytes } from '@/features/admissions/wizard'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'
import { formatDate, formatDateTime } from '@/utils/format'

const ACTION_LABEL: Partial<Record<AdmissionStatus, string>> = {
  reviewing: 'Start the review',
  waitlisted: 'Put on the waiting list',
  accepted: 'Accept',
  rejected: 'Reject',
}

/** One application: the family's data and documents, decisions, notes and history, enrolment. */
export function AdmissionDetailPage() {
  const id = Number(useParams().id)
  const app = useQuery(applicationQuery(id))
  useDocumentTitle(app.data ? app.data.fullName : 'Application')

  if (app.isPending) return <LoadingState />
  if (app.error)
    return toApiError(app.error).status === 404 ? (
      <NotFoundState homeHref="/admin/admissions" resource="application" />
    ) : (
      <ErrorState onRetry={() => void app.refetch()} />
    )
  return <ApplicationView app={app.data} />
}

function ApplicationView({ app }: { app: ApplicationDetail }) {
  const { t } = useTranslation('admissions')
  const change = useChangeApplicationStatus(app.id)
  const [rejecting, setRejecting] = useState(false)
  const [enrolling, setEnrolling] = useState(false)
  const relation = (r: string) => t(GUARDIAN_RELATIONS.find((x) => x.value === r)?.label ?? 'Other')
  const toBring = app.requiredDocuments.filter((d) => !d.upload)

  const move = (status: AdmissionStatus, reason?: string) =>
    change.mutate(
      { status, reason: reason ?? null },
      {
        onSuccess: () => {
          toast.success(t('Application updated'), t(admissionStatusLabel[status]))
          setRejecting(false)
        },
        onError: (e) => toast.error(t('Could not update the application'), toApiError(e).message),
      },
    )

  return (
    <>
      <PageHeader
        title={app.fullName}
        breadcrumbs={[{ label: t('Online admissions'), to: '/admin/admissions' }, { label: app.reference }]}
        homeHref="/admin"
        meta={
          <>
            <Badge tone={admissionStatusTone[app.status]} dot>
              {t(admissionStatusLabel[app.status])}
            </Badge>
            <span className="font-mono text-xs text-muted">{app.reference}</span>
            <span className="text-xs text-muted">
              {app.className} · {app.academicYear.name} ·{' '}
              {t('sent on {{date}}', { date: formatDateTime(app.submittedAt) })}
            </span>
          </>
        }
        actions={
          <>
            {app.transitions
              .filter((s) => s !== 'accepted')
              .map((s) => (
                <Button
                  key={s}
                  variant={s === 'rejected' ? 'danger-soft' : 'secondary'}
                  leftIcon={s === 'rejected' ? <XCircle /> : <Clock />}
                  loading={change.isPending && change.variables.status === s}
                  onClick={() => (s === 'rejected' ? setRejecting(true) : move(s))}
                >
                  {t(ACTION_LABEL[s] ?? s)}
                </Button>
              ))}
            {app.transitions.includes('accepted') ? (
              <Button
                leftIcon={<CheckCircle2 />}
                loading={change.isPending && change.variables.status === 'accepted'}
                disabled={app.placesLeft === 0}
                title={app.placesLeft === 0 ? t('No places left in this class') : undefined}
                onClick={() => move('accepted')}
              >
                {t('Accept')}
              </Button>
            ) : null}
            {app.status === 'accepted' ? (
              <Button variant="gradient" leftIcon={<UserPlus />} onClick={() => setEnrolling(true)}>
                {t('Enrol in a section')}
              </Button>
            ) : null}
          </>
        }
      />

      {app.status === 'enrolled' && app.studentId ? (
        <Alert
          tone="success"
          className="mb-4"
          title={t('Enrolled in {{section}}', { section: app.sectionName ?? app.className })}
        >
          <Link to={`/admin/students/${app.studentId}`} className="font-medium underline">
            {t('Open the student record')}
          </Link>
        </Alert>
      ) : null}
      {app.status === 'rejected' && app.decisionReason ? (
        <Alert tone="danger" className="mb-4" title={t('Reason given to the family')}>
          {app.decisionReason}
        </Alert>
      ) : null}
      {app.placesLeft !== null && ['submitted', 'reviewing', 'waitlisted'].includes(app.status) ? (
        <p className="mb-4 text-sm text-muted">
          {t('{{count}} places left in {{class}}', { count: app.placesLeft, class: app.className })}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-5">
          <Card>
            <CardHeader title={t('Student')} icon={<School />} />
            <CardBody>
              <DescriptionList
                items={[
                  { label: t('Name'), value: app.fullName },
                  {
                    label: t('Gender'),
                    value: t(app.gender === 'female' ? 'Female' : app.gender === 'male' ? 'Male' : 'Other'),
                  },
                  { label: t('Date of birth'), value: formatDate(app.dateOfBirth) },
                  { label: t('Place of birth'), value: app.placeOfBirth },
                  { label: t('Nationality'), value: app.nationality },
                  { label: t('Phone'), value: app.phone },
                  { label: t('Email'), value: app.email },
                  {
                    label: t('Previous school'),
                    value: [app.previousSchool, app.previousClass].filter(Boolean).join(' · ') || null,
                  },
                  { label: t('Address'), value: app.address, span: 2 },
                ]}
              />
            </CardBody>
          </Card>

          {app.guardians.length ? (
            <Card>
              <CardHeader title={t('Parents and guardians')} icon={<Users />} />
              <CardBody className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {app.guardians.map((g, i) => (
                  <div key={i} className="min-w-0 rounded-control border border-line p-3 text-sm">
                    <p className="font-medium text-ink">
                      {g.name}
                      {g.isPrimary ? (
                        <Badge tone="brand" size="sm" className="ml-2">
                          {t('Main contact')}
                        </Badge>
                      ) : null}
                    </p>
                    <p className="text-xs text-muted">{relation(g.relation)}</p>
                    {g.phone ? (
                      <a
                        href={`tel:${g.phone.replace(/\s/g, '')}`}
                        className="mt-1 block text-brand-700 hover:underline"
                      >
                        {g.phone}
                      </a>
                    ) : null}
                    {g.email ? <p className="break-all text-ink-soft">{g.email}</p> : null}
                    {g.occupation ? <p className="text-ink-soft">{g.occupation}</p> : null}
                  </div>
                ))}
              </CardBody>
            </Card>
          ) : null}

          <Card>
            <CardHeader title={t('Documents')} icon={<FileText />} />
            <CardBody className="space-y-2">
              {app.requiredDocuments
                .filter((d) => d.upload)
                .map((d) => {
                  const file = app.documents.find((f) => f.index === d.index)
                  return (
                    <div
                      key={d.index}
                      className="flex min-w-0 flex-wrap items-center gap-2 rounded-control border border-line p-3 text-sm"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-ink">{d.label}</span>
                        <span className="block truncate text-xs text-muted">
                          {file
                            ? `${file.name} · ${formatBytes(file.size)}`
                            : d.required
                              ? t('Missing')
                              : t('Not sent')}
                        </span>
                      </span>
                      {file?.url ? (
                        <Button variant="secondary" size="sm" leftIcon={<Download />} asChild>
                          <a href={file.url} target="_blank" rel="noreferrer">
                            {t('Open')}
                          </a>
                        </Button>
                      ) : null}
                    </div>
                  )
                })}
              {toBring.length ? (
                <p className="pt-2 text-xs text-muted">
                  {t('To bring to the school office')}: {toBring.map((d) => d.label).join(', ')}
                </p>
              ) : null}
            </CardBody>
          </Card>
        </div>

        <History app={app} />
      </div>

      <RejectDialog
        open={rejecting}
        onOpenChange={setRejecting}
        loading={change.isPending}
        onConfirm={(reason) => move('rejected', reason)}
      />
      {enrolling ? <EnrolDialog app={app} onClose={() => setEnrolling(false)} /> : null}
    </>
  )
}

function History({ app }: { app: ApplicationDetail }) {
  const { t } = useTranslation('admissions')
  const note = useAddApplicationNote(app.id)
  const [body, setBody] = useState('')
  return (
    <Card className="h-fit">
      <CardHeader title={t('Notes and history')} icon={<MessageSquarePlus />} />
      <CardBody className="space-y-4">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (!body.trim()) return
            note.mutate(
              { body },
              {
                onSuccess: () => setBody(''),
                onError: (err) => toast.error(t('Could not add the note'), toApiError(err).message),
              },
            )
          }}
          className="space-y-2"
        >
          <FormField label={t('Add a note for the office')} hideLabel>
            <Textarea
              rows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t('Add a note for the office (not shown to the family)')}
            />
          </FormField>
          <Button
            type="submit"
            size="sm"
            variant="secondary"
            loading={note.isPending}
            disabled={!body.trim()}
          >
            {t('Add the note')}
          </Button>
        </form>
        <ol className="space-y-3 border-l border-line pl-4">
          {[...app.events].reverse().map((e) => (
            <li key={e.id} className="relative text-sm">
              <span
                aria-hidden="true"
                className={cn(
                  'absolute top-1.5 -left-[1.3rem] size-2.5 rounded-full ring-2 ring-surface',
                  e.kind === 'note' ? 'bg-subtle' : 'bg-brand-500',
                )}
              />
              {e.kind === 'status' && e.to ? (
                <p className="text-ink">
                  {e.from
                    ? t('{{from}} → {{to}}', {
                        from: t(admissionStatusLabel[e.from]),
                        to: t(admissionStatusLabel[e.to]),
                      })
                    : t('Application received')}
                </p>
              ) : null}
              {e.body ? (
                <p className={cn('whitespace-pre-line', e.kind === 'note' ? 'text-ink' : 'text-ink-soft')}>
                  {e.body}
                </p>
              ) : null}
              <p className="text-xs text-muted">
                {formatDateTime(e.createdAt)} · {e.author ?? t('The family')}
              </p>
            </li>
          ))}
        </ol>
      </CardBody>
    </Card>
  )
}

function RejectDialog({
  open,
  onOpenChange,
  loading,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  loading: boolean
  onConfirm: (reason: string) => void
}) {
  const { t } = useTranslation('admissions')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | undefined>()
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('Reject the application')}
      description={t('The family is emailed the decision with this reason.')}
      icon={<XCircle />}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('Cancel')}
          </Button>
          <Button
            variant="danger"
            loading={loading}
            onClick={() => {
              if (!reason.trim()) {
                setError('Give the reason: the family will read it.')
                return
              }
              onConfirm(reason.trim())
            }}
          >
            {t('Reject')}
          </Button>
        </>
      }
    >
      <FormField label={t('Reason')} error={error} required>
        <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} />
      </FormField>
    </Dialog>
  )
}

function EnrolDialog({ app, onClose }: { app: ApplicationDetail; onClose: () => void }) {
  const { t } = useTranslation('admissions')
  const sections = useQuery(applicationSectionsQuery(app.id))
  const enrol = useEnrolApplication(app.id)
  const [sectionId, setSectionId] = useState<number | null>(null)
  const [rollNumber, setRollNumber] = useState('')
  const [portal, setPortal] = useState(true)
  const [notify, setNotify] = useState(true)
  const [error, setError] = useState<string | undefined>()

  const submit = () => {
    if (!sectionId) {
      setError('Choose a section.')
      return
    }
    enrol.mutate(
      { sectionId, rollNumber: rollNumber.trim() || null, createPortalAccount: portal, notifyFamily: notify },
      {
        onSuccess: (r) => {
          toast.success(
            t('{{name}} is enrolled', { name: r.student.fullName }),
            r.portalAccount?.invited
              ? t('An invitation to the family portal was sent to {{email}}.', {
                  email: r.portalAccount.email,
                })
              : undefined,
          )
          onClose()
        },
        onError: (e) => {
          const err = toApiError(e)
          setError(err.fieldErrors().sectionId ?? err.message)
        },
      },
    )
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => (o ? undefined : onClose())}
      size="lg"
      title={t('Enrol {{name}}', { name: app.fullName })}
      description={t(
        'Choose the section of {{class}} for {{year}}. The student record is created with the guardians.',
        {
          class: app.className,
          year: app.academicYear.name,
        },
      )}
      icon={<UserPlus />}
      dismissible={!enrol.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={enrol.isPending}>
            {t('Cancel')}
          </Button>
          <Button
            variant="gradient"
            loading={enrol.isPending}
            onClick={submit}
            className="h-auto min-h-10 whitespace-normal"
          >
            {t('Enrol the student')}
          </Button>
        </>
      }
    >
      {sections.isPending ? (
        <LoadingState />
      ) : (
        <div className="space-y-4">
          <fieldset>
            <legend className="mb-2 text-[0.8125rem] font-medium text-ink-soft">{t('Section')}</legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup">
              {(sections.data ?? []).map((s) => {
                const full = s.seatsLeft === 0
                return (
                  <label
                    key={s.id}
                    className={cn(
                      'flex min-w-0 cursor-pointer flex-col rounded-control border p-3 text-sm',
                      sectionId === s.id ? 'border-brand-500 ring-2 ring-brand-200' : 'border-line',
                      full && 'cursor-not-allowed opacity-60',
                    )}
                  >
                    <input
                      type="radio"
                      name="section"
                      className="sr-only"
                      disabled={full}
                      checked={sectionId === s.id}
                      onChange={() => {
                        setSectionId(s.id)
                        setError(undefined)
                      }}
                    />
                    <span className="font-medium text-ink">{s.displayName}</span>
                    <span className={cn('text-xs', full ? 'text-danger-600' : 'text-muted')}>
                      {s.capacity === null
                        ? t('{{count}} students, no limit', { count: s.enrolled })
                        : full
                          ? t('Full ({{capacity}} seats)', { capacity: s.capacity })
                          : t('{{enrolled}} / {{capacity}} seats taken', {
                              enrolled: s.enrolled,
                              capacity: s.capacity,
                            })}
                    </span>
                  </label>
                )
              })}
            </div>
            {error ? (
              <p role="alert" className="mt-2 text-xs font-medium text-danger-600">
                {t(error)}
              </p>
            ) : null}
          </fieldset>
          <FormField label={t('Roll number')} hint={t('Optional')}>
            <Input
              value={rollNumber}
              onChange={(e) => setRollNumber(e.target.value)}
              className="sm:max-w-40"
            />
          </FormField>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <Checkbox
                id="enrol-portal"
                checked={portal}
                onCheckedChange={(v) => setPortal(v === true)}
                className="mt-0.5"
              />
              <label htmlFor="enrol-portal" className="text-ink-soft">
                {t('Create the family portal account and email an invitation')}
              </label>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="enrol-notify"
                checked={notify}
                onCheckedChange={(v) => setNotify(v === true)}
                className="mt-0.5"
              />
              <label htmlFor="enrol-notify" className="text-ink-soft">
                {t('Email the placement to the family')}
              </label>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  )
}
